/**
 * @file: color-mode.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { kPreferredColorModeKey } from '@euler/functions/constants';
import { serviceFactory } from '@euler/services/factory';
import { useEffect, useState } from 'react';
import { Appearance, Platform } from 'react-native';
import KeyboardManager from 'react-native-keyboard-manager';

export type ColorMode = 'light' | 'dark';

const kDarkModeEnabled = false;

type ColorModeListener = (codeMode: ColorMode) => void;

export class ColorModeManager {
  static Key = kPreferredColorModeKey;

  static default = new ColorModeManager();

  private _current: ColorMode | undefined;
  private readonly _listeners: ColorModeListener[] = [];

  get current(): ColorMode | undefined {
    return this._current;
  }

  set current(colorMode: ColorMode | undefined) {
    this._current = colorMode;
  }

  async get(): Promise<ColorMode | undefined> {
    try {
      return (
        (await serviceFactory.defaultStorageService.get<ColorMode>(
          ColorModeManager.Key,
        )) ?? undefined
      );
    } catch {
      return undefined;
    }
  }

  async set(value: ColorMode) {
    try {
      await serviceFactory.defaultStorageService.set(
        ColorModeManager.Key,
        value,
      );
      this._current = value;
      for (const listener of this._listeners) {
        listener(value);
      }
    } catch (e) {
      console.error(e);
    }
  }

  addListener(listener: ColorModeListener) {
    this._listeners.push(listener);
    return {
      remove: () => {
        const index = this._listeners.indexOf(listener);
        if (index >= 0) {
          this._listeners.splice(index, 1);
        }
      },
    };
  }
}

export const useColorMode = (defaultColorMode?: ColorMode) => {
  const [colorMode, setColorMode] = useState<ColorMode>();

  useEffect(() => {
    void (async () => {
      const result = await ColorModeManager.default.get();
      const value =
        result ??
        defaultColorMode ??
        (Platform.OS === 'web' ? 'light' : Appearance.getColorScheme()) ??
        'light';
      setColorMode(value);
      ColorModeManager.default.current = value;
    })();

    if (Platform.OS === 'web') return;

    const appearanceListener = Appearance.addChangeListener(settings => {
      if (settings.colorScheme) {
        setColorMode(settings.colorScheme);
        ColorModeManager.default.current = settings.colorScheme;
        if (kDarkModeEnabled && Platform.OS === 'ios') {
          KeyboardManager.setKeyboardAppearance(settings.colorScheme);
        }
      }
    });

    const colorModeListener = ColorModeManager.default.addListener(newValue => {
      setColorMode(newValue);
    });

    return () => {
      appearanceListener.remove();
      colorModeListener.remove();
    };
  }, [colorMode, defaultColorMode]);

  return colorMode;
};
